import { useCallback, useEffect, useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { useAuthLink } from 'lib/comlink/AuthLink';

enum SideNavStates {
    off = 'off',
    pinned = 'pinned',
    collapsed = 'collapsed'
}

const MESSAGE_EVENT = 'hb-sidenav-toggle';

export const useSideNavSupport = () => {
    const [sidenavState, setSidenavState] = useState(() => {
        const params = new URLSearchParams(global?.location?.search);
        return params.get('sidenav') || SideNavStates.off;
    });

    useEffect(() => {
        function onMessage(e: MessageEvent) {
            if (e.data.event === MESSAGE_EVENT) {
                setSidenavState(e.data.payload.pinned ? SideNavStates.pinned : SideNavStates.collapsed);
            }
        }
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
        };
    }, [setSidenavState]);

    useEffect(() => {
        document.body.setAttribute('sidenav-state', sidenavState);
    }, [sidenavState]);

    useEffectOnce(() => {
        window.parent.postMessage({ event: `${MESSAGE_EVENT}:get-state` }, '*');
    });

    useAuthLinkSupport(sidenavState, setSidenavState);
};

function useAuthLinkSupport(sidenavState: string, setSidenavState: (state: SideNavStates) => void) {
    const authLink = useAuthLink();

    const onSidenavChange = useCallback(
        (state: unknown) => {
            setSidenavState(state as SideNavStates);
        },
        [setSidenavState]
    );

    useEffect(() => {
        authLink?.addCallback('sideNaveStateChange', onSidenavChange);
    }, [authLink, onSidenavChange]);

    useEffect(() => {
        document.body.setAttribute('sidenav-state', sidenavState);
    }, [sidenavState]);

    useEffect(() => {
        if (authLink) {
            authLink.getSidenavState()?.then(state => {
                setSidenavState(state as SideNavStates);
            });
        }
    }, [authLink, setSidenavState]);
}
